import { DateTimePicker, Feedback } from 'canoe';
import { Field, FieldAttributes, useFormikContext } from 'formik';
import get from 'lodash/get';
import has from 'lodash/has';
import isNumber from 'lodash/isNumber';
import moment from 'moment';
import React, { useMemo } from 'react';

import { DefinitionFormikValues } from 'dashboard/pages/Segment/components/types';

import { DefinitionFieldWrapper } from './DefinitionFormComponents';

interface DefinitionDatePickerProps {
  name: string;
  readOnly?: boolean;
  placeholder?: string;
}

export const DefinitionDatePicker = ({
  name,
  readOnly,
  placeholder,
}: DefinitionDatePickerProps) => {
  const { values, setFieldValue } = useFormikContext<DefinitionFormikValues>();

  const currentValue = useMemo(() => {
    const value = get(values, name);

    // if it's an empty value, a number (e.g. switching from relative date) or it's not a valid date
    // datepicker crashes if value is not Date | undefined
    if (value === '' || isNumber(value) || !moment(value).isValid()) {
      return undefined;
    }
    return value;
  }, [values, name]);

  if (!has(values, name)) {
    setFieldValue(name, null, false);
  }

  return (
    <Field name={name}>
      {({ field, meta }: FieldAttributes<any>) => (
        <DefinitionFieldWrapper>
          <DateTimePicker
            {...field}
            data-cy="segment-definition-date"
            mode="datetime"
            selectedDate={currentValue}
            onConfirm={(date) => setFieldValue(name, date, meta.touched && meta.error)}
            disabled={readOnly}
            placeholder={placeholder}
            state={meta.touched && meta.error ? 'danger' : 'default'}
            formatDate={(date) => {
              if (!date) {
                return '';
              }

              const month = date.getMonth() + 1;
              const day = date.getDate();
              const dateString = `${month < 10 ? '0' : ''}${month}/${
                day < 10 ? '0' : ''
              }${day}/${date.getFullYear()}`;

              const hours = date.getHours();
              const minutes = date.getMinutes();
              const timeString = `${hours < 10 ? '0' : ''}${hours}:${
                minutes < 10 ? '0' : ''
              }${minutes}`;

              return `${dateString}, ${timeString}`;
            }}
          />
          {meta.touched && meta.error && (
            <Feedback mt="xxs" color="danger">
              {meta.error}
            </Feedback>
          )}
        </DefinitionFieldWrapper>
      )}
    </Field>
  );
};
